export type HowItWorksLanguage = "en" | "ms" | "sabah" | "zh";

type Step = { title: string; description: string };
type Outcome = { label: string; title: string; description: string };
type HowItWorksCopy = {
  languageAria: string;
  back: string;
  heroTitle: string;
  heroBody: string;
  heroAction: string;
  memberEyebrow: string;
  memberTitle: string;
  memberBody: string;
  steps: Step[];
  collectiveEyebrow: string;
  collectiveTitle: string;
  collectiveBody: string;
  outcomes: Outcome[];
  finalEyebrow: string;
  finalTitle: string;
  finalBody: string;
  join: string;
  footer: string;
};

export const HOW_IT_WORKS_COPY: Record<HowItWorksLanguage, HowItWorksCopy> = {
  en: {
    languageAria: "Choose language",
    back: "Back to home",
    heroTitle: "Your everyday needs become collective buying power",
    heroBody: "Share what matters to your household. Kitanego safely pools member demand to negotiate stronger deals with suppliers—alerting you the moment better pricing is unlocked.",
    heroAction: "How Kitanego works",
    memberEyebrow: "01 · MEMBER INPUT",
    memberTitle: "Submit your monthly demand.",
    memberBody: "Step-by-step planning that adds valuable context—without committing you to an order or payment.",
    steps: [
      { title: "Register", description: "Create your Kitanego account with basic details." },
      { title: "State your district", description: "Tell us where your household demand comes from." },
      { title: "Choose products", description: "Select the everyday products you genuinely need." },
      { title: "Choose brands", description: "Share the brands you prefer or are open to buying." },
      { title: "Buying frequency", description: "Tell us how often you normally buy each product." },
      { title: "Planned amount", description: "Estimate how much you expect to spend or purchase." },
      { title: "Share your opinion", description: "Tell us how interested you are in a better price." },
      { title: "Update monthly", description: "Refresh your wishlist whenever your needs change." },
    ],
    collectiveEyebrow: "02 · COLLECTIVE ACTION",
    collectiveTitle: "Individual needs. Powerful, united negotiation.",
    collectiveBody: "Instead of looking at isolated member profiles, Kitanego pools and analyses collective demand to identify the products and brands our community wants most.",
    outcomes: [
      { label: "Combine & summarise", title: "Demand becomes clear", description: "Product, brand, frequency and planned-amount signals reveal meaningful demand patterns." },
      { label: "Supplier negotiation", title: "Kitanego negotiates", description: "Strong demand evidence is taken to suppliers to seek the best possible price for selected essentials." },
      { label: "Minimum requirement", title: "The target is reached", description: "A pricing opportunity moves forward when supplier requirements and minimum demand are fulfilled." },
      { label: "Member update", title: "You are notified", description: "Members receive pricing updates when a top-selected product qualifies for a supplier offer." },
    ],
    finalEyebrow: "SHARED NEEDS · SMARTER PRICES",
    finalTitle: "Ready to make a difference?",
    finalBody: "Start your wishlist today and help show suppliers the power of our community’s demand.",
    join: "Join now",
    footer: "Demand better prices, together.",
  },
  ms: {
    languageAria: "Pilih bahasa",
    back: "Kembali ke laman utama",
    heroTitle: "Keperluan harian anda menjadi kuasa pembelian kolektif",
    heroBody: "Kongsikan perkara yang penting untuk isi rumah anda. Kitanego menggabungkan permintaan ahli dengan selamat untuk merundingkan tawaran yang lebih baik dengan pembekal—dan memaklumkan anda apabila harga yang lebih baik tersedia.",
    heroAction: "Cara Kitanego berfungsi",
    memberEyebrow: "01 · INPUT AHLI",
    memberTitle: "Hantar permintaan bulanan anda.",
    memberBody: "Perancangan langkah demi langkah yang menambah konteks berguna—tanpa mewajibkan pesanan atau bayaran.",
    steps: [
      { title: "Daftar", description: "Cipta akaun Kitanego menggunakan butiran asas." },
      { title: "Nyatakan daerah", description: "Beritahu kami daerah asal permintaan isi rumah anda." },
      { title: "Pilih produk", description: "Pilih produk harian yang benar-benar anda perlukan." },
      { title: "Pilih jenama", description: "Kongsikan jenama pilihan atau jenama yang anda sanggup beli." },
      { title: "Kekerapan pembelian", description: "Beritahu kami kekerapan anda membeli setiap produk." },
      { title: "Jumlah dirancang", description: "Anggarkan jumlah perbelanjaan atau pembelian anda." },
      { title: "Kongsi pendapat", description: "Nyatakan tahap minat anda terhadap harga yang lebih baik." },
      { title: "Kemas kini bulanan", description: "Kemas kini senarai keperluan apabila keperluan anda berubah." },
    ],
    collectiveEyebrow: "02 · TINDAKAN KOLEKTIF",
    collectiveTitle: "Keperluan individu. Rundingan bersatu yang lebih berkuasa.",
    collectiveBody: "Daripada melihat profil ahli secara berasingan, Kitanego menggabungkan dan menganalisis permintaan kolektif untuk mengenal pasti produk dan jenama yang paling diperlukan komuniti.",
    outcomes: [
      { label: "Gabung & ringkaskan", title: "Permintaan menjadi jelas", description: "Isyarat produk, jenama, kekerapan dan jumlah dirancang menunjukkan corak permintaan yang bermakna." },
      { label: "Rundingan pembekal", title: "Kitanego berunding", description: "Bukti permintaan yang kukuh dibawa kepada pembekal untuk mendapatkan harga terbaik bagi barangan terpilih." },
      { label: "Keperluan minimum", title: "Sasaran dicapai", description: "Peluang harga diteruskan apabila syarat pembekal dan permintaan minimum dipenuhi." },
      { label: "Kemas kini ahli", title: "Anda dimaklumkan", description: "Ahli menerima kemas kini harga apabila produk pilihan utama layak menerima tawaran pembekal." },
    ],
    finalEyebrow: "KEPERLUAN BERSAMA · HARGA LEBIH BIJAK",
    finalTitle: "Bersedia untuk membawa perubahan?",
    finalBody: "Mulakan senarai keperluan hari ini dan tunjukkan kepada pembekal kuasa permintaan komuniti kita.",
    join: "Sertai sekarang",
    footer: "Tuntut harga yang lebih baik, bersama-sama.",
  },
  sabah: {
    languageAria: "Pilih bahasa",
    back: "Kembali ke laman utama",
    heroTitle: "Keperluan harian kamu jadi kuasa membeli bersama",
    heroBody: "Kasi tau apa yang penting untuk keluarga kamu. Kitanego gabungkan permintaan ahli dengan selamat untuk runding tawaran lebih bagus sama pembekal—dan kasi tau kamu bila harga lebih baik sudah ada.",
    heroAction: "Macam mana Kitanego berfungsi",
    memberEyebrow: "01 · INPUT AHLI",
    memberTitle: "Hantar permintaan bulanan kamu.",
    memberBody: "Perancangan langkah demi langkah yang kasi maklumat lebih berguna—tanpa wajib membuat pesanan atau bayaran.",
    steps: [
      { title: "Daftar", description: "Buat akaun Kitanego guna butiran asas kamu." },
      { title: "Pilih daerah", description: "Kasi tau permintaan keluarga kamu datang dari daerah mana." },
      { title: "Pilih produk", description: "Pilih produk harian yang kamu betul-betul perlukan." },
      { title: "Pilih jenama", description: "Kongsi jenama pilihan atau jenama yang kamu sudi beli." },
      { title: "Kekerapan membeli", description: "Kasi tau berapa kerap kamu biasa beli setiap produk." },
      { title: "Jumlah dirancang", description: "Anggar berapa banyak kamu rancang belanja atau beli." },
      { title: "Kongsi pendapat", description: "Kasi tau tahap minat kamu untuk dapat harga lebih bagus." },
      { title: "Kemas kini bulanan", description: "Kemas kini senarai keperluan bila keperluan kamu berubah." },
    ],
    collectiveEyebrow: "02 · TINDAKAN BERSAMA",
    collectiveTitle: "Keperluan sendiri-sendiri. Rundingan bersama yang lebih kuat.",
    collectiveBody: "Daripada tengok profil ahli satu-satu, Kitanego gabung dan analisis permintaan bersama untuk cari produk dan jenama yang komuniti paling perlu.",
    outcomes: [
      { label: "Gabung & ringkaskan", title: "Permintaan jadi jelas", description: "Isyarat produk, jenama, kekerapan dan jumlah dirancang tunjuk corak permintaan yang berguna." },
      { label: "Rundingan pembekal", title: "Kitanego berunding", description: "Bukti permintaan yang kuat dibawa kepada pembekal untuk cari harga terbaik bagi barangan pilihan." },
      { label: "Keperluan minimum", title: "Sasaran sudah dicapai", description: "Peluang harga diteruskan bila syarat pembekal dan permintaan minimum sudah dipenuhi." },
      { label: "Kemas kini ahli", title: "Kamu akan diberitahu", description: "Ahli dapat kemas kini harga bila produk pilihan utama layak menerima tawaran pembekal." },
    ],
    finalEyebrow: "KEPERLUAN BERSAMA · HARGA LEBIH BAGUS",
    finalTitle: "Sudah sedia mau buat perubahan?",
    finalBody: "Mulakan senarai keperluan hari ini dan tunjuk sama pembekal kuatnya permintaan komuniti kita.",
    join: "Sertai sekarang",
    footer: "Minta harga lebih bagus, sama-sama.",
  },
  zh: {
    languageAria: "选择语言",
    back: "返回主页",
    heroTitle: "您的日常需求汇聚成集体购买力",
    heroBody: "分享家庭真正需要的商品。Kitanego 安全汇总会员需求，与供应商争取更优惠的价格，并在优惠落实时通知您。",
    heroAction: "Kitanego 如何运作",
    memberEyebrow: "01 · 会员需求",
    memberTitle: "提交您的每月需求。",
    memberBody: "通过清晰步骤提供有用信息—不代表下单，也无需付款。",
    steps: [
      { title: "注册", description: "使用基本资料创建 Kitanego 账户。" },
      { title: "选择地区", description: "告诉我们您的家庭需求来自沙巴哪个地区。" },
      { title: "选择产品", description: "选择您真正需要的日常产品。" },
      { title: "选择品牌", description: "分享您偏好或愿意购买的品牌。" },
      { title: "购买频率", description: "告诉我们您通常多久购买一次产品。" },
      { title: "计划数量", description: "估算您预计的购买量或消费范围。" },
      { title: "分享意见", description: "告诉我们您对更优惠价格的兴趣程度。" },
      { title: "每月更新", description: "需求改变时，随时更新您的愿望清单。" },
    ],
    collectiveEyebrow: "02 · 集体行动",
    collectiveTitle: "个人需求，汇聚成更强的议价力量。",
    collectiveBody: "Kitanego 不会孤立地看待个别会员资料，而是汇总并分析集体需求，找出社区最需要的产品和品牌。",
    outcomes: [
      { label: "汇总与整理", title: "需求变得清晰", description: "产品、品牌、频率和计划数量的数据形成有意义的需求趋势。" },
      { label: "供应商谈判", title: "Kitanego 进行议价", description: "我们以明确的需求证据与供应商洽谈，为指定必需品争取更优惠价格。" },
      { label: "最低要求", title: "达到目标", description: "当供应商条件及最低需求量获得满足后，优惠机会便会继续推进。" },
      { label: "会员通知", title: "您会收到通知", description: "当热门产品符合供应商优惠条件时，会员将收到价格更新。" },
    ],
    finalEyebrow: "共同需求 · 更聪明的价格",
    finalTitle: "准备一起带来改变吗？",
    finalBody: "立即建立愿望清单，让供应商看见我们社区需求的力量。",
    join: "立即加入",
    footer: "携手争取更优惠价格。",
  },
};
